import graphql__621d6d941c2208e6922f from "./__generated__/EmailTemplateDetailsView_emailTemplate.graphql.ts";
import graphql__ed4f43aad36b06e6e726 from "./__generated__/EmailTemplateDetailsViewAssignLayoutMutation.graphql.ts";
import graphql__2f3cfdf4317b135eacb4 from "./__generated__/EmailTemplateDetailsViewSetPrimaryLanguageMutation.graphql.ts";
import graphql__fbba1f6dbc84e834b103 from "./__generated__/EmailTemplateDetailsViewRemoveVariantMutation.graphql.ts";import * as React from "react";
import {graphql} from "react-relay";
import {useOutletContext} from "react-router";
import {connect, ConnectedProps} from "react-redux";
import {hideModal, showModal} from "../store/actions";
import {useFragment, useMutation, useRelayEnvironment} from "react-relay/hooks";
import ActionButton from "./presentational/button/ActionButton";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTrash, faPlusCircle} from "@fortawesome/pro-regular-svg-icons";
import {faStar as faStarSolid} from "@fortawesome/pro-solid-svg-icons";
import {useErrorContext} from "../contexts/ErrorContext";
import {commitMutationWithErrorHandling} from "../helper/commitMutationWithErrorHandling";
import {useIntl} from "react-intl";
import DeleteEmailTemplateDialog from "./dialog/DeleteEmailTemplateDialog";
import EmailTemplateVariantPanel from "./email-template/EmailTemplateVariantPanel";
import AddEmailTemplateVariantDialog from "./dialog/AddEmailTemplateVariantDialog";
import AbstractCombobox from "./abstract/AbstractCombobox";

type Props = {}

const connector = connect(undefined, {
    dispatchShowModal: showModal,
    dispatchHideModal: hideModal
});

type EmailTemplateDetailsViewProps = Props & ConnectedProps<typeof connector>;

const EmailTemplateDetailsView: React.FC<EmailTemplateDetailsViewProps> = (props) => {
    const intl = useIntl();
    const context: any = useOutletContext();
    const {addError} = useErrorContext();
    const emailTemplate = useFragment(
        graphql__621d6d941c2208e6922f,
        context.emailTemplate
    );
    const emailLayouts = context.emailLayouts || [];
    const relayEnvironment = useRelayEnvironment();
    const {dispatchShowModal, dispatchHideModal} = props;

    const [commitAssignLayout] = useMutation(graphql__ed4f43aad36b06e6e726);

    const [selectedLanguage, setSelectedLanguage] = React.useState<string | null>(null);

    // Set initial selected language to primary language
    React.useEffect(() => {
        if (emailTemplate.primaryLanguage && !selectedLanguage) {
            setSelectedLanguage(emailTemplate.primaryLanguage);
        } else if (emailTemplate.variants.length > 0 && !selectedLanguage) {
            setSelectedLanguage(emailTemplate.variants[0].language);
        }
    }, [emailTemplate.primaryLanguage, emailTemplate.variants, selectedLanguage]);

    const showDeleteEmailTemplateDialog = () => {
        dispatchShowModal({
            body: (<DeleteEmailTemplateDialog queryRef={emailTemplate} handleCloseDialog={dispatchHideModal}/>)
        })
    };

    const showAddVariantDialog = () => {
        const existingLanguages = emailTemplate.variants.map(v => v.language);
        dispatchShowModal({
            body: (<AddEmailTemplateVariantDialog
                emailTemplateId={emailTemplate.id}
                existingLanguages={existingLanguages}
                handleCloseDialog={dispatchHideModal}
            />)
        })
    };

    const getLanguageLabel = (code: string) => {
        const labels: Record<string, string> = {
            'de': 'Deutsch',
            'en': 'English',
        };
        return labels[code] || code.toUpperCase();
    };

    const canDelete = emailTemplate.usageCount === 0;
    const selectedVariant = emailTemplate.variants.find(v => v.language === selectedLanguage);

    const handleLayoutChange = (layout: any) => {
        commitAssignLayout({
            variables: {
                input: {
                    id: emailTemplate.id,
                    emailLayoutIdentifier: layout?.emailLayoutIdentifier || null
                }
            },
            onError: () => {
                addError({message: intl.formatMessage({id: 'errors.failedToAssignLayout'}), type: 'general'});
            }
        });
    };

    const layoutDataProvider = async (query: string) => {
        const lowerQuery = query.toLowerCase();
        return emailLayouts.filter(layout =>
            layout.name.toLowerCase().includes(lowerQuery)
        );
    };

    const handleSetPrimaryLanguage = (language: string) => {
        commitMutationWithErrorHandling(
            relayEnvironment,
            {
                mutation: graphql__2f3cfdf4317b135eacb4,
                variables: {
                    input: {
                        id: emailTemplate.id,
                        language
                    }
                },
                onCompleted: () => {},
                onError: () => {}
            },
            addError,
            {
                customErrorMessage: "errors.failedToSetPrimaryLanguage"
            }
        );
    };

    const handleRemoveVariant = (language: string) => {
        if (emailTemplate.variants.length <= 1) {
            addError({message: intl.formatMessage({id: 'errors.cannotRemoveLastVariant'}), type: 'validation'});
            return;
        }

        commitMutationWithErrorHandling(
            relayEnvironment,
            {
                mutation: graphql__fbba1f6dbc84e834b103,
                variables: {
                    input: {
                        id: emailTemplate.id,
                        language
                    }
                },
                onCompleted: () => {
                    // If we removed the selected language, switch to another one
                    if (selectedLanguage === language) {
                        const remainingVariant = emailTemplate.variants.find(v => v.language !== language);
                        if (remainingVariant) {
                            setSelectedLanguage(remainingVariant.language);
                        }
                    }
                },
                onError: () => {}
            },
            addError,
            {
                customErrorMessage: "errors.failedToRemoveVariant"
            }
        );
    };

    return (
        <>
            {/* Layout Section */}
            <div className="border border-gray-200 rounded-lg bg-white mb-6">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-900">
                        {intl.formatMessage({id: 'emailTemplate.section.layout'})}
                    </h4>
                </div>
                <div className="px-4 py-4">
                    <div className="max-w-md">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {intl.formatMessage({id: 'emailTemplate.field.emailLayout'})}
                        </label>
                        <AbstractCombobox
                            dataProvider={layoutDataProvider}
                            getEntryId={(entry: any) => entry.emailLayoutIdentifier}
                            getEntryLabel={(entry: any) => entry?.name || ''}
                            getEntrySecondaryLabel={(entry: any) => entry?.isDefault ? intl.formatMessage({id: 'emailLayout.badge.default'}) : undefined}
                            initialValue={emailTemplate.emailLayout ? emailLayouts.find(l => l.emailLayoutIdentifier === emailTemplate.emailLayout.emailLayoutIdentifier) : emailLayouts.find(l => l.isDefault) || null}
                            onValueChange={handleLayoutChange}
                        />
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="border border-gray-200 rounded-lg bg-white">
                {/* Language tabs header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-900">
                        {intl.formatMessage({id: 'emailTemplate.section.content'})}
                    </h4>
                </div>

                {/* Language tabs */}
                <div className="flex border-b border-gray-200 bg-white px-2">
                    {emailTemplate.variants.map((variant) => (
                        <button
                            key={variant.language}
                            type="button"
                            onClick={() => setSelectedLanguage(variant.language)}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                                selectedLanguage === variant.language
                                    ? "border-red-600 text-red-600"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                        >
                            {getLanguageLabel(variant.language)}
                            {variant.language === emailTemplate.primaryLanguage && (
                                <FontAwesomeIcon
                                    icon={faStarSolid}
                                    className="w-3 h-3 text-amber-500"
                                    title={intl.formatMessage({id: 'emailTemplate.primaryBadge'})}
                                />
                            )}
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={showAddVariantDialog}
                        className="px-3 py-2 text-sm font-medium border-b-2 border-transparent text-gray-400 hover:text-red-600 transition-colors"
                        title={intl.formatMessage({id: 'button.addLanguage'})}
                    >
                        <FontAwesomeIcon icon={faPlusCircle} className="w-4 h-4" />
                    </button>
                </div>

                {/* Variant content */}
                {selectedVariant && (
                    <EmailTemplateVariantPanel
                        emailTemplateId={emailTemplate.id}
                        language={selectedVariant.language}
                        subject={selectedVariant.subject}
                        preheader={selectedVariant.preheader || ''}
                        htmlContent={selectedVariant.htmlContent}
                        textContent={selectedVariant.textContent}
                        layoutTextContent={emailTemplate.emailLayout?.textContent || ''}
                        isPrimary={selectedVariant.language === emailTemplate.primaryLanguage}
                        canRemove={emailTemplate.variants.length > 1}
                        images={emailTemplate.images}
                        onSetPrimary={() => handleSetPrimaryLanguage(selectedVariant.language)}
                        onRemove={() => handleRemoveVariant(selectedVariant.language)}
                    />
                )}
            </div>

            <div className="flex space-x-4 justify-end">
                {canDelete && (
                    <ActionButton onClick={showDeleteEmailTemplateDialog} icon={faTrash} label={intl.formatMessage({id: 'button.deleteEmailTemplate'})}/>
                )}
            </div>
        </>
    )
}

export default connector(EmailTemplateDetailsView);
